import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import { Reveal, PageShell, EASE } from "@/components/Reveal";

const ABSCHNITT_1 = [
  "Ich war schon immer neugierig auf das Abenteuer Leben – und ich liebe es, dieses Leben auf vielfältige Weise zu erfahren und zu genießen, mit all den Höhen und Tiefen, die dazugehören.",
  "Schon früh haben mich Fragen bewegt: Was lässt uns wirklich gesund, frei und lebendig sein? Was trägt uns, wenn das Leben uns herausfordert? Und wie finden wir immer wieder zurück zu uns selbst?",
  "Mit Anfang 20 fand der Buddhismus mich – mitten in einer persönlichen Krise und in dem Wissen: So kann es nicht weitergehen. Ich begegnete großartigen Lehrern, reiste mit ihnen um die Welt und lernte eine Sicht auf das Leben kennen, die mich tief geprägt hat. Meditation und buddhistische Praxis wurden zu einem unverzichtbaren Teil meines Lebens.",
  "Fast zeitgleich, Anfang der Neunzigerjahre, entdeckte ich Shiatsu. Damit begann für mich eine ganz andere Reise – hinein in den Körper. Ich lernte, seine Sprache feiner wahrzunehmen, und entdeckte über die fernöstliche Sichtweise eine tiefe Verbindung zwischen Mensch und Natur, den Jahreszeiten und den Elementen.",
  "Einige Jahre später kam Yoga in mein Leben. Ich liebe Bewegung und Tanz – schon als Jugendliche hatte ich die Füße gefühlt häufiger in der Luft als auf dem Boden. Yoga eröffnete mir jedoch weit mehr als Bewegung: eine weitere Möglichkeit, Körper, Atem und Bewusstsein miteinander zu verbinden.",
  "Und meine Reise ging weiter – im Innen wie im Außen. Coaching, Atem- und Bewusstseinsarbeit, somatische Ansätze und die Natur erweiterten meinen Weg.",
  "Eine besondere Rolle spielen dabei die Pferde. Sie begleiten mich seit meiner Kindheit. Ihre Präsenz, Klarheit und Feinfühligkeit haben mich vieles über Erdung, Vertrauen, Grenzen, Verbindung und echtes Gegenwärtigsein gelehrt.",
];

const ABSCHNITT_2 = [
  "Seit über 30 Jahren begleite ich Menschen in Einzelsitzungen, Seminaren und Retreats. Aus meinem eigenen Lebensweg, meinen vielfältigen Ausbildungen und all den Menschen, denen ich begegnen durfte, ist über die Jahre meine ganz persönliche Art der Begleitung entstanden.",
  "Ich verbinde fundiertes Wissen und langjährige Erfahrung mit Intuition und einer feinen Wahrnehmung für den Menschen, der mir begegnet.",
  "Dabei ist mir eines besonders wichtig geblieben: den Menschen als Ganzes zu sehen – mit seinem Körper, seinem inneren Erleben, seiner Geschichte und seiner ganz eigenen Kraft.",
  "Für mich ist der Körper dabei weit mehr als etwas, das uns durchs Leben trägt. Er ist Ausdruck unseres inneren Erlebens – und für mich auch ein Zuhause der Seele.",
];

export default function UeberMich() {
  return (
    <PageShell testid="ueber-mich-page">
      <PageHero title="Über mich" testid="ueber-mich-hero" />

      <section data-testid="ueber-mich-content" className="px-6 pb-28 md:px-12 md:pb-40">
        <div className="mx-auto grid max-w-6xl items-start gap-14 md:grid-cols-12 md:gap-10 lg:gap-16">
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.1, ease: EASE }}
              className="relative"
            >
              <div
                aria-hidden="true"
                className="absolute -left-4 -top-4 h-full w-full rounded-t-full border border-rot/25"
              />
              <div
                data-testid="ueber-mich-portrait"
                className="relative overflow-hidden rounded-t-full"
              >
                <motion.img
                  src="/images/carmen-efeu.webp"
                  alt="Portrait von Carmen, lachend inmitten von grünem Efeu"
                  className="aspect-[3/4] w-full object-cover"
                  initial={{ scale: 1.15 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.6, ease: EASE }}
                />
              </div>
              <p
                data-testid="ueber-mich-foto-credit"
                className="mt-3 text-xs tracking-wide text-anthrazit/50"
              >
                Foto: Sabine Brauns
              </p>
            </motion.div>
          </div>

          <div className="md:col-span-7 lg:col-span-6">
            {ABSCHNITT_1.map((text, i) => (
              <Reveal key={i} delay={i * 0.05} className={i === 0 ? "" : "mt-7"}>
                <p className="text-base leading-loose text-anthrazit/85 md:text-lg">{text}</p>
              </Reveal>
            ))}
            <Reveal>
              <h2
                data-testid="ueber-mich-zwischentitel"
                className="mb-8 mt-16 font-serif text-3xl leading-tight text-rot md:text-4xl"
              >
                Was mich heute trägt
              </h2>
            </Reveal>
            {ABSCHNITT_2.map((text, i) => (
              <Reveal key={i} delay={i * 0.05} className={i === 0 ? "" : "mt-7"}>
                <p className="text-base leading-loose text-anthrazit/85 md:text-lg">{text}</p>
              </Reveal>
            ))}
            <Reveal delay={0.15}>
              <p
                data-testid="ueber-mich-highlight"
                className="my-10 border-l-2 border-rot pl-7 font-serif text-xl italic leading-relaxed text-rot md:my-12 md:pl-9 md:text-2xl"
              >
                Dein Körper spricht mit dir.
                <br />
                Bist du bereit, ihm zu lauschen?
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-base leading-loose text-anthrazit/85 md:text-lg">
                Ich freue mich auf dich!
              </p>
            </Reveal>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
